import React, { Component } from "react";
import { connect } from "react-redux";
import {
  Card,
  Checkbox,
  Col,
  DatePicker,
  Empty,
  PageHeader,
  Row,
  Spin,
  Tag,
  Tooltip,
  message,
  Table,
} from "antd";
import {
  DownloadOutlined,
  FilePdfOutlined,
  ReloadOutlined,
} from "@ant-design/icons";
import axios from "axios";
import moment from "moment";
import "moment/locale/es";

import pdfMake from "pdfmake/build/pdfmake";
import pdfFonts from "pdfmake/build/vfs_fonts";
import * as XLSXStyle from "xlsx-js-style";

import ButtonItem from "../../../Components/ButtonItem";
import SelectEmpleadosItem from "../../../Components/SelectEmpleadosItem";
import SelectDepartamentosItem from "../../../Components/SelectDepartamentosItem";

pdfMake.vfs = pdfFonts.pdfMake.vfs;
moment.locale("es");

const { RangePicker } = DatePicker;

// Color y estilo por letra
const LETRA_CONFIG = {
  A: { color: "#d32f2f", label: "Ausente",          bg: "#ffebee" },
  "<": { color: "#f57c00", label: "Retardo",         bg: "#fff3e0" },
  "+": { color: "#388e3c", label: "Tiempo extra",    bg: "#e8f5e9" },
  R:  { color: "#f57c00", label: "Retardo (horas)",  bg: "#fff3e0" },
  E:  { color: "#388e3c", label: "Tiempo extra (horas)", bg: "#e8f5e9" },
  ST: { color: "#f57c00", label: "Salida temprana (horas)", bg: "#fff3e0" },
  "[": { color: "#7b1fa2", label: "Faltó entrada",   bg: "#f3e5f5" },
  "]": { color: "#7b1fa2", label: "Faltó salida",    bg: "#f3e5f5" },
  P:  { color: "#1565c0", label: "Presente",         bg: "#e3f2fd" },
  D:  { color: "#5d4037", label: "Día de descanso",  bg: "#efebe9" },
  W:  { color: "#546e7a", label: "Fin de semana",    bg: "#eceff1" },
  H:  { color: "#ad1457", label: "Día festivo",      bg: "#fce4ec" },
  V:  { color: "#00897b", label: "Vacaciones",       bg: "#e0f2f1" },
};

const tokenCfg = (token) => {
  const mR = /^([\d.]+)R$/.exec(token);
  if (mR) return { cfg: LETRA_CONFIG.R, label: `Retardo: ${mR[1]} h` };
  const mE = /^([\d.]+)E$/.exec(token);
  if (mE) return { cfg: LETRA_CONFIG.E, label: `Tiempo extra: ${mE[1]} h` };
  const mST = /^([\d.]+)ST$/.exec(token);
  if (mST) return { cfg: LETRA_CONFIG.ST, label: `Salida temprana: ${mST[1]} h` };
  const cfg = LETRA_CONFIG[token] || { color: "#666", label: token, bg: "#f5f5f5" };
  return { cfg, label: cfg.label };
};

const renderLetras = (valor) => {
  if (!valor || valor === "—") return <span style={{ color: "#bdbdbd" }}>—</span>;

  const tokens = valor.split(",").map(l => l.trim()).filter(Boolean);

  return (
    <div style={{ display: "flex", gap: 2, justifyContent: "center", flexWrap: "wrap" }}>
      {tokens.map((t, i) => {
        const { cfg, label } = tokenCfg(t);
        return (
          <Tooltip key={i} title={label}>
            <span style={{
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
              minWidth: 22, height: 22,
              padding: "0 4px",
              borderRadius: 4,
              backgroundColor: cfg.bg,
              color: cfg.color,
              fontWeight: 700,
              fontSize: 12,
              cursor: "default",
              userSelect: "none",
            }}>
              {t}
            </span>
          </Tooltip>
        );
      })}
    </div>
  );
};

class AsistenciaDiaria extends Component {
  state = {
    departamentosIds: [],
    departamentoNombre: "",
    empleadosIds: [],
    empleadoNombre: "",
    todosEmpleados: false,
    rango: moment().isoWeekday() === 1
      ? [moment().subtract(1, "week").startOf("isoWeek"), moment().subtract(1, "week").endOf("isoWeek")]
      : [moment().startOf("isoWeek"), moment()],
    loading: false,
    dias: [],    // ['2025-03-01', ...]
    filas: [],   // [{ empleadoId, nombre, departamento, dias: { '2025-03-01': 'P', ... } }]
  };

  componentDidMount() {
    this.consultar();
  }

  // =========================
  // Helpers
  // =========================
  hexRgb = (hex) => hex.replace("#", "").toUpperCase();

  xlsxCellStyle = (valor) => {
    if (!valor || valor === "—") return null;
    const firstToken = valor.split(",")[0].trim();
    const mR = /^([\d.]+)R$/.exec(firstToken);
    const mE = /^([\d.]+)E$/.exec(firstToken);
    const mST = /^([\d.]+)ST$/.exec(firstToken);
    let cfg;
    if (mR) cfg = LETRA_CONFIG.R;
    else if (mE) cfg = LETRA_CONFIG.E;
    else if (mST) cfg = LETRA_CONFIG.ST;
    else cfg = LETRA_CONFIG[firstToken] || { color: "#666666", bg: "#f5f5f5" };
    return {
      font: { bold: true, color: { rgb: this.hexRgb(cfg.color) }, sz: 9 },
      fill: { fgColor: { rgb: this.hexRgb(cfg.bg) } },
      alignment: { horizontal: "center", vertical: "center" },
    };
  };

  disabledDate = (current) => {
    if (current && current.isAfter(moment().endOf("day"))) return true;
    const { rango } = this.state;
    const start = rango?.[0];
    if (start) {
      const max = moment(start).add(30, "days").endOf("day");
      return current && current.isAfter(max);
    }
    return false;
  };

  // =========================
  // API
  // =========================
  consultar = async () => {
    const { empresaId, headersToken } = this.props;
    const { rango, departamentosIds, empleadosIds, todosEmpleados } = this.state;

    if (!rango?.[0] || !rango?.[1]) {
      message.warning("Selecciona un rango de fechas.");
      return;
    }
    if (!todosEmpleados && !departamentosIds?.length && !empleadosIds?.length) {
      message.warning("Selecciona al menos un departamento o empleado, o marca Todos los empleados.");
      return;
    }

    this.setState({ loading: true });

    try {
      const res = await axios.post(
        "reportesAsistencia/reporteAsistenciaDiaria",
        {
          empresaId,
          departamentosIds: departamentosIds || [],
          empleadosIds: empleadosIds || [],
          todosEmpleados,
          fechaInicio: rango[0].format("YYYY-MM-DD"),
          fechaFin: rango[1].format("YYYY-MM-DD"),
        },
        { headers: headersToken }
      );

      if (res.data?.success !== 2) {
        message.error(res.data?.message || "Error al consultar el reporte.");
        this.setState({ dias: [], filas: [] });
        return;
      }

      const { dias, filas } = res.data.data;
      this.setState({
        dias: Array.isArray(dias) ? dias : [],
        filas: Array.isArray(filas) ? filas : [],
      });
    } catch (e) {
      message.error("No se pudo cargar el reporte.");
      this.setState({ dias: [], filas: [] });
    } finally {
      this.setState({ loading: false });
    }
  };

  // =========================
  // Events
  // =========================
  onChangeDepartamento = (value, option) => {
    this.setState(
      {
        departamentosIds: value || [],
        departamentoNombre: option?.label || option?.children || "",
        empleadosIds: [],
        empleadoNombre: "",
      },
      () => this.consultar()
    );
  };

  onChangeEmpleado = (value, option) => {
    this.setState(
      { empleadosIds: value || [], empleadoNombre: option?.label || option?.children || "" },
      () => this.consultar()
    );
  };

  onChangeTodosEmpleados = (e) => {
    const todosEmpleados = e.target.checked;
    // Al marcarlo el reporte sale de todos, asi que los filtros de departamento
    // y empleado se limpian para que no se contradigan con el check
    this.setState(
      {
        todosEmpleados,
        departamentosIds: [],
        departamentoNombre: "",
        empleadosIds: [],
        empleadoNombre: "",
      },
      // Al desmarcar ya no queda filtro, no tiene sentido consultar (solo avisaria)
      () => { if (todosEmpleados) this.consultar(); }
    );
  };

  onChangeRango = (value) => {
    this.setState({ rango: value }, () => {
      if (value?.[0] && value?.[1]) this.consultar();
    });
  };

  // =========================
  // EXPORT: Excel 1 — reporte completo con colores
  // =========================
  exportExcel1 = () => {
    const { dias, filas, rango } = this.state;
    if (!filas?.length) { message.warning("No hay datos para exportar."); return; }

    const headerStyle = {
      font: { bold: true, color: { rgb: "FFFFFF" }, sz: 9 },
      fill: { fgColor: { rgb: "1A3A4A" } },
      alignment: { horizontal: "center", vertical: "center", wrapText: true },
    };
    const fixedHeaderStyle = { ...headerStyle, alignment: { horizontal: "left", vertical: "center" } };
    const fixedCellStyle = { font: { sz: 9 }, alignment: { horizontal: "left", vertical: "center" } };

    const headerRow = [
      { v: "Nombre", s: fixedHeaderStyle },
      { v: "Departamento", s: fixedHeaderStyle },
      { v: "Turno", s: fixedHeaderStyle },
      ...dias.map(d => ({ v: moment(d).format("DD/MM") + "\n" + moment(d).format("ddd"), s: headerStyle })),
    ];

    const rows = [headerRow];

    filas.forEach(f => {
      const row = [
        { v: f.nombre || "—", s: fixedCellStyle },
        { v: f.departamento || "—", s: fixedCellStyle },
        { v: f.turno || "—", s: fixedCellStyle },
      ];
      dias.forEach(d => {
        const valor = f.dias?.[d];
        const s = this.xlsxCellStyle(valor);
        row.push(s ? { v: valor, s } : { v: "", s: { font: { sz: 9 }, alignment: { horizontal: "center" } } });
      });
      rows.push(row);
    });

    const ws = XLSXStyle.utils.aoa_to_sheet(rows);
    ws["!cols"] = [{ wch: 26 }, { wch: 20 }, { wch: 18 }, ...dias.map(() => ({ wch: 7 }))];
    ws["!rows"] = [{ hpt: 28 }];

    const wb = XLSXStyle.utils.book_new();
    XLSXStyle.utils.book_append_sheet(wb, ws, "Asistencia");
    XLSXStyle.writeFile(wb, `asistencia_${rango[0].format("YYYYMMDD")}_${rango[1].format("YYYYMMDD")}.xlsx`);
  };

  // =========================
  // EXPORT: Excel 2 — solo incidencias
  // =========================
  exportExcel2 = () => {
    const { dias, filas, rango } = this.state;
    if (!filas?.length) { message.warning("No hay datos para exportar."); return; }

    const SIN_INCIDENCIA = new Set(["P", "D", "W"]);
    const esIncidencia = (valor) => {
      if (!valor || valor === "—") return false;
      return valor.split(",").some(t => !SIN_INCIDENCIA.has(t.trim()));
    };

    const headerStyle = {
      font: { bold: true, color: { rgb: "FFFFFF" }, sz: 9 },
      fill: { fgColor: { rgb: "7B1FA2" } },
      alignment: { horizontal: "center", vertical: "center", wrapText: true },
    };
    const fixedHeaderStyle = { ...headerStyle, alignment: { horizontal: "left", vertical: "center" } };
    const fixedCellStyle = { font: { sz: 9 }, alignment: { horizontal: "left", vertical: "center" } };

    const headerRow = [
      { v: "Nombre", s: fixedHeaderStyle },
      { v: "Departamento", s: fixedHeaderStyle },
      { v: "Turno", s: fixedHeaderStyle },
      ...dias.map(d => ({ v: moment(d).format("DD/MM") + "\n" + moment(d).format("ddd"), s: headerStyle })),
    ];

    const rows = [headerRow];

    filas.forEach(f => {
      if (!dias.some(d => esIncidencia(f.dias?.[d]))) return;

      const row = [
        { v: f.nombre || "—", s: fixedCellStyle },
        { v: f.departamento || "—", s: fixedCellStyle },
        { v: f.turno || "—", s: fixedCellStyle },
      ];
      dias.forEach(d => {
        const valor = f.dias?.[d];
        if (esIncidencia(valor)) {
          const s = this.xlsxCellStyle(valor);
          row.push(s ? { v: valor, s } : { v: valor, s: { font: { sz: 9 }, alignment: { horizontal: "center" } } });
        } else {
          row.push({ v: "", s: { font: { sz: 9 }, alignment: { horizontal: "center" } } });
        }
      });
      rows.push(row);
    });

    if (rows.length === 1) { message.info("No hay incidencias en el período seleccionado."); return; }

    const ws = XLSXStyle.utils.aoa_to_sheet(rows);
    ws["!cols"] = [{ wch: 26 }, { wch: 20 }, { wch: 18 }, ...dias.map(() => ({ wch: 7 }))];
    ws["!rows"] = [{ hpt: 28 }];

    const wb = XLSXStyle.utils.book_new();
    XLSXStyle.utils.book_append_sheet(wb, ws, "Incidencias");
    XLSXStyle.writeFile(wb, `incidencias_${rango[0].format("YYYYMMDD")}_${rango[1].format("YYYYMMDD")}.xlsx`);
  };

  // =========================
  // EXPORT: PDF
  // =========================
  exportPDF = () => {
    const { dias, filas, rango, departamentoNombre, empleadoNombre } = this.state;
    if (!filas?.length) { message.warning("No hay datos para exportar."); return; }

    const header = (t) => ({ text: t, bold: true, fontSize: 6 });

    // Encabezados: Nombre, Depto, Turno + un encabezado por día
    const headerRow = [
      header("Nombre"),
      header("Departamento"),
      header("Turno"),
      ...dias.map(d => header(moment(d).format("DD/MM\nddd"))),
    ];

    const body = [headerRow];

    filas.forEach(f => {
      body.push([
        { text: f.nombre || "—", fontSize: 6 },
        { text: f.departamento || "—", fontSize: 6 },
        { text: f.turno || "—", fontSize: 6 },
        ...dias.map(d => ({ text: f.dias?.[d] || "—", fontSize: 6, alignment: "center" })),
      ]);
    });

    pdfMake.createPdf({
      pageSize: "A3",
      pageOrientation: "landscape",
      pageMargins: [12, 40, 12, 26],
      content: [
        { text: "Reporte: Resumen de Asistencia", style: "title" },
        {
          columns: [
            {
              width: "*",
              text: [
                { text: "Departamento: ", bold: true }, departamentoNombre || "Todos", "\n",
                { text: "Empleado: ", bold: true }, empleadoNombre || "Todos",
              ],
              fontSize: 8,
            },
            {
              width: "auto",
              text: [
                { text: "Rango: ", bold: true },
                `${rango?.[0]?.format("DD/MM/YYYY")} - ${rango?.[1]?.format("DD/MM/YYYY")}`,
              ],
              fontSize: 8,
            },
          ],
          margin: [0, 6, 0, 4],
        },
        // Leyenda
        {
          text: "A=Ausente  NR=Retardo (N horas)  NE=Tiempo extra (N horas)  NST=Salida temprana (N horas)  [=Faltó entrada  ]=Faltó salida  P=Presente  D=Descanso  W=Fin de semana  H=Festivo",
          fontSize: 6,
          color: "#666",
          margin: [0, 0, 0, 8],
        },
        { table: { headerRows: 1, body }, layout: "lightHorizontalLines" },
        {
          text: `Generado: ${moment().format("DD/MM/YYYY HH:mm")}`,
          style: "footer",
          margin: [0, 8, 0, 0],
        },
      ],
      styles: {
        title: { fontSize: 13, bold: true },
        footer: { fontSize: 8, color: "#666666" },
      },
      defaultStyle: { fontSize: 6 },
    }).download(`resumen_asistencia_${rango[0].format("YYYYMMDD")}_${rango[1].format("YYYYMMDD")}.pdf`);
  };

  // =========================
  // Columnas dinámicas
  // =========================
  getColumns = () => {
    const { dias } = this.state;

    const fijas = [
      {
        title: "Nombre", dataIndex: "nombre", key: "nombre",
        width: 180, ellipsis: true, fixed: "left",
        sorter: (a, b) => (a.nombre || "").localeCompare(b.nombre || ""),
      },
      {
        title: "Departamento", dataIndex: "departamento", key: "departamento",
        width: 150, ellipsis: true, fixed: "left",
        sorter: (a, b) => (a.departamento || "").localeCompare(b.departamento || ""),
      },
      {
        title: "Turno", dataIndex: "turno", key: "turno",
        width: 140, ellipsis: true, fixed: "left",
        sorter: (a, b) => (a.turno || "").localeCompare(b.turno || ""),
        render: (v) => v && v !== "—"
          ? <span style={{ fontSize: 12 }}>{v}</span>
          : <span style={{ color: "#bdbdbd" }}>—</span>,
      },
    ];

    const dinamicas = dias.map(dia => {
      const m = moment(dia);
      const esDomingo = m.day() === 0;
      const esSabado = m.day() === 6;
      return {
        title: (
          <div style={{ textAlign: "center", lineHeight: 1.3 }}>
            <div style={{ fontWeight: 700, fontSize: 12 }}>{m.format("DD")}</div>
            <div style={{
              fontSize: 10,
              color: esDomingo || esSabado ? "#f57c00" : "#666",
              fontWeight: esDomingo || esSabado ? 600 : 400,
            }}>
              {m.format("ddd")}
            </div>
          </div>
        ),
        key: dia,
        width: 54,
        align: "center",
        render: (_, row) => renderLetras(row.dias?.[dia]),
      };
    });

    return [...fijas, ...dinamicas];
  };

  // =========================
  // Leyenda visual
  // =========================
  renderLeyenda = () => (
    <div style={{ display: "flex", flexWrap: "wrap", gap: 8, padding: "8px 12px", borderBottom: "1px solid #f0f0f0" }}>
      {Object.entries(LETRA_CONFIG).map(([letra, cfg]) => (
        <Tooltip key={letra} title={cfg.label}>
          <span style={{
            display: "inline-flex", alignItems: "center", gap: 4,
            fontSize: 11, color: "#555", cursor: "default",
          }}>
            <span style={{
              display: "inline-flex", alignItems: "center", justifyContent: "center",
              width: 20, height: 20, borderRadius: 4,
              backgroundColor: cfg.bg, color: cfg.color,
              fontWeight: 700, fontSize: 11,
            }}>
              {letra}
            </span>
            {cfg.label}
          </span>
        </Tooltip>
      ))}
    </div>
  );

  // =========================
  // Render
  // =========================
  render() {
    const { loading, rango, departamentosIds, empleadosIds, todosEmpleados, dias, filas } = this.state;

    // Scroll horizontal: 180 + 150 + 140 (fijas) + 54 * días
    const scrollX = 470 + dias.length * 54;

    return (
      <>
        <PageHeader
          title="Resumen de Asistencia"
          style={{
            border: "1px solid rgb(235, 237, 240)",
            backgroundColor: "white",
            marginBottom: 10,
          }}
          extra={
            <>
              <Tooltip title="Consultar">
                <ButtonItem icon={<ReloadOutlined />} onClick={this.consultar} loading={loading}>
                  Consultar
                </ButtonItem>
              </Tooltip>
              <Tooltip title="Exportar reporte completo de asistencia a Excel">
                <ButtonItem type="default" icon={<DownloadOutlined />} onClick={this.exportExcel1} disabled={!filas?.length}>
                  Excel asistencia
                </ButtonItem>
              </Tooltip>
              <Tooltip title="Exportar solo incidencias a Excel (ausencias, retardos, etc.)">
                <ButtonItem type="default" icon={<DownloadOutlined />} onClick={this.exportExcel2} disabled={!filas?.length}>
                  Excel incidencias
                </ButtonItem>
              </Tooltip>
              <Tooltip title="Exportar a PDF">
                <ButtonItem type="default" icon={<FilePdfOutlined />} onClick={this.exportPDF} disabled={!filas?.length}>
                  PDF
                </ButtonItem>
              </Tooltip>
            </>
          }
        />

        <Row gutter={[10, 10]}>
          {/* Filtros */}
          <Col xs={24}>
            <Card title="Filtros" size="small" style={{ borderRadius: 6, boxShadow: "0 1px 2px rgba(0,0,0,0.05)" }}>
              <Row gutter={[16, 12]} align="bottom">
                <Col xs={24} md={8}>
                  <div style={{ fontSize: 12, color: "#666", marginBottom: 4 }}>Departamento</div>
                  <SelectDepartamentosItem
                    empresaId={this.props.empresaId}
                    headersToken={this.props.headersToken}
                    sinBorde
                    value={departamentosIds}
                    onChange={this.onChangeDepartamento}
                    allowClear
                    width="100%"
                    placeholder="Todos los departamentos"
                    mode="multiple"
                    disabled={todosEmpleados}
                  />
                </Col>
                <Col xs={24} md={8}>
                  <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 4 }}>
                    <span style={{ fontSize: 12, color: "#666" }}>Empleado</span>
                    <Checkbox
                      checked={todosEmpleados}
                      onChange={this.onChangeTodosEmpleados}
                      style={{ fontSize: 12 }}
                    >
                      Todos los empleados
                    </Checkbox>
                  </div>
                  <SelectEmpleadosItem
                    empresaId={this.props.empresaId}
                    headersToken={this.props.headersToken}
                    sinBorde
                    value={empleadosIds}
                    onChange={this.onChangeEmpleado}
                    allowClear
                    width="100%"
                    placeholder="Todos los empleados"
                    mode="multiple"
                    disabled={todosEmpleados}
                  />
                </Col>
                <Col xs={24} md={8}>
                  <div style={{ fontSize: 12, color: "#666", marginBottom: 4 }}>Rango de fechas (máx. 31 días)</div>
                  <RangePicker
                    value={rango}
                    onChange={this.onChangeRango}
                    style={{ width: "100%" }}
                    format="DD/MM/YYYY"
                    disabledDate={this.disabledDate}
                    allowClear={false}
                  />
                </Col>
              </Row>
            </Card>
          </Col>

          {/* Tabla */}
          <Col xs={24}>
            <Card
              title="Resultados"
              size="small"
              extra={
                loading
                  ? <Tag color="processing">Cargando...</Tag>
                  : <Tag>{filas?.length || 0} empleados</Tag>
              }
              bodyStyle={{ padding: 0 }}
              style={{ minHeight: "80vh", borderRadius: 6, boxShadow: "0 1px 2px rgba(0,0,0,0.05)" }}
            >
              <Spin spinning={loading}>
                {this.renderLeyenda()}
                {filas?.length
                  ? (
                    <Table
                      size="small"
                      rowKey={(r, idx) => `${r.empleadoId}-${idx}`}
                      columns={this.getColumns()}
                      dataSource={filas}
                      loading={loading}
                      pagination={{ pageSize: 50, showSizeChanger: true, pageSizeOptions: [20, 50, 100] }}
                      scroll={{ x: scrollX, y: "calc(100vh - 380px)" }}
                      sticky
                    />
                  )
                  : (
                    <div style={{ padding: 40 }}>
                      <Empty description="Selecciona filtros y consulta para ver resultados" />
                    </div>
                  )
                }
              </Spin>
            </Card>
          </Col>
        </Row>
      </>
    );
  }
}

const mapStateToProps = (state) => ({
  headersToken: state.user.headersToken,
  empresaId: state.empresa.id,
});

export default connect(mapStateToProps)(AsistenciaDiaria);